import React from 'react';
import { Link } from 'react-router-dom';
import { Gauge, Users, Eye, Target, PieChart, PhoneCall, ArrowRight } from 'lucide-react';
import useInView from '../hooks/useInView';

/* Round-dominant glyphs throughout — Gauge, Users, Eye, Target, PieChart,
   PhoneCall. Beyond the look, a circular mark carries ink through its middle,
   which is exactly the band that stays in frame when the ghost scales up, so
   these flood far more evenly than the angular Zap / Database / BarChart2
   they replace.

   All six run through one card now. Each still takes a different hue —
   adjacent cards are never shades of one colour — and the accent drives both
   the card's bottom rule and the colour its icon floods with on hover. The
   order matches the chart series, so a feature keeps its colour wherever it
   appears on the site. */
const FEATURES = [
  {
    icon: Gauge,
    accent: 'accent-1',
    title: 'Auto dialer',
    description:
      'Automate outbound calls and maximise agent talk time. Unanswered numbers are skipped automatically, so your team stays in back-to-back conversations.',
  },
  {
    icon: Users,
    accent: 'accent-5',
    title: 'CRM & lead management',
    description:
      'Upload, organise and assign leads in one place. Track every interaction, set follow-up reminders, and never let a hot lead go cold.',
  },
  {
    icon: Eye,
    accent: 'accent-4',
    title: 'Live monitoring dashboard',
    description:
      'Track every agent and every call in real time. See who is live, who is idle, call durations and conversion rates — all from one screen.',
  },
  {
    icon: Target,
    accent: 'accent-3',
    title: 'Campaign management',
    description:
      'Create flexible calling campaigns in minutes. Set targets, assign lead lists, schedule time slots and run several campaigns at once.',
  },
  {
    icon: PieChart,
    accent: 'accent-6',
    title: 'Reports & analytics',
    description:
      'Performance insight per agent and per campaign. Know exactly what is working, who is performing, and where the next hour of coaching should go.',
  },
  {
    icon: PhoneCall,
    accent: 'accent-8',
    title: 'Mobile-based calling',
    description:
      'Agents work from anywhere using their own Android phones. No desk phones, no SIM boxes, no hardware — just install and dial.',
  },
];

const FeaturesSection = () => {
  const [ref, inView] = useInView();

  return (
  <section ref={ref} className={`section-hero section section--surface reveal ${inView ? 'is-inview' : ''}`}>
    {/* Half-strength aurora marks this as a section hero without
        competing with the page hero above. */}
    <span className="hero-aurora reveal-bg" aria-hidden="true" />
    <div className="container-page">
      <div className="section-head">
        <span className="eyebrow accent-brand">
          <span className="spark-mark" aria-hidden="true" />
          Platform features
        </span>
        <h2 className="section-title">
          Everything your team needs.
          <br />
          <span className="display-accent">Nothing they don’t.</span>
        </h2>
        <p className="section-sub">
          Built specifically for outbound calling operations on Android — not ported from a desktop
          app.
        </p>
      </div>

      {/* One grid, one card. The bento split that used to put two features on
          artwork cards and four on plain ones is gone — it meant this section
          showed two different card designs at once. */}
      <div className="stagger-children grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map((feature, i) => {
          const Icon = feature.icon;
          return (
            <article
              key={feature.title}
              className={`${feature.accent} feature-card reveal-color`}
              style={{ '--reveal-delay': `${i * 80}ms` }}
            >
              <span className="feature-card__bg" aria-hidden="true">
                <Icon />
              </span>
              <div className="feature-card__content">
                <h3 className="feature-card__title">{feature.title}</h3>
                <p className="feature-card__body">{feature.description}</p>
              </div>
            </article>
          );
        })}
      </div>

      <div className="mt-10 text-center">
        <Link to="/features" className="btn-pill btn-pill--white">
          Explore all features
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </section>
  );
};

export default FeaturesSection;
